/**
 * NOS-118: React hook to fetch and update org AI profile.
 */
import { useState, useEffect, useCallback } from "react"
import { supabase } from "@/lib/supabase"
import { useOrgStore } from "@/stores/orgStore"
import type { OrgAIProfile } from "@/types/orgProfile"
import type { TablesInsert } from "@/types/database"

export interface UseOrgAIProfileResult {
  profile: OrgAIProfile | null
  isLoading: boolean
  error: string | null
  updateProfile: (data: Partial<OrgAIProfile>) => Promise<void>
}

export function useOrgAIProfile(): UseOrgAIProfileResult {
  const orgId = useOrgStore((s) => s.currentOrg?.id ?? null)
  const [profile, setProfile] = useState<OrgAIProfile | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!orgId) {
      setProfile(null)
      setIsLoading(false)
      return
    }
    setIsLoading(true)
    setError(null)
    const { data, error: err } = await supabase
      .from("org_ai_profiles")
      .select("*")
      .eq("org_id", orgId)
      .maybeSingle()

    if (err) {
      setError(err.message)
      setProfile(null)
    } else {
      setProfile(data as OrgAIProfile | null)
    }
    setIsLoading(false)
  }, [orgId])

  useEffect(() => {
    fetch()
  }, [fetch])

  const updateProfile = useCallback(
    async (data: Partial<OrgAIProfile>) => {
      if (!orgId) throw new Error("No organization selected")
      const payload: TablesInsert<"org_ai_profiles"> = {
        org_id: orgId,
        mission: data.mission ?? null,
        vision: data.vision ?? null,
        values: data.values ?? null,
        who_served: data.who_served ?? null,
        years_operating: data.years_operating ?? null,
        impact_stats: data.impact_stats ?? null,
        tone: data.tone ?? null,
        updated_at: new Date().toISOString(),
      }
      const { error: err } = await supabase
        .from("org_ai_profiles")
        .upsert(payload, { onConflict: "org_id" })

      if (err) throw new Error(err.message)
      await fetch()
    },
    [orgId, fetch]
  )

  return { profile, isLoading, error, updateProfile }
}
